// Direct & indirect objects — telling them apart, the two pronoun sets
// (lo/la/los/las vs le/les), how they combine (le/les → se), and the
// redundant-pronoun habit. A side-by-side pronoun table closes the page.
import { renderGuideSections } from "../ui/guideSections.js";

export function objectsView(app) {
  const table = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 mt-6">
      <h3 class="text-lg font-bold text-sage-800 mb-3">Object Pronouns at a Glance</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Person</th>
          <th class="py-2 px-3">Direct (what)</th>
          <th class="py-2 px-3">Indirect (to/for whom)</th>
        </tr></thead>
        <tbody>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">me</td><td class="py-2 px-3">mi</td><td class="py-2 px-3">mi</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">you (inf.)</td><td class="py-2 px-3">ti</td><td class="py-2 px-3">ti</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">him / it</td><td class="py-2 px-3">lo</td><td class="py-2 px-3">gli</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">her / it / you (formal)</td><td class="py-2 px-3">la</td><td class="py-2 px-3">le</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">us</td><td class="py-2 px-3">ci</td><td class="py-2 px-3">ci</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">you all</td><td class="py-2 px-3">vi</td><td class="py-2 px-3">vi</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">them (m.)</td><td class="py-2 px-3">li</td><td class="py-2 px-3">gli / loro</td></tr>
          <tr><td class="py-2 pr-3 font-medium">them (f.)</td><td class="py-2 px-3">le</td><td class="py-2 px-3">gli / loro</td></tr>
        </tbody>
      </table></div>
      <p class="text-sage-500 text-xs mt-3">Only the third person really differs (lo/la/li/le vs gli/le). When both combine, <b>gli/le + lo → glielo</b>: <i>Glielo do.</i></p>
    </div>`;

  renderGuideSections(
    app,
    "Direct & Indirect Objects",
    "Most verbs act on something. The direct object is what the action lands on; the indirect object is who benefits from it. Italian marks the two with different pronouns — and the difference only really shows up in the third person.",
    [
      {
        title: "1. Spotting which is which",
        body: `
          <p class="text-sage-700 mb-2">Ask two questions about the verb:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5 mb-2">
            <li><b>What / whom?</b> → direct object. <i>Compro <u>il pane</u>.</i> (I buy the bread.)</li>
            <li><b>To whom / for whom?</b> → indirect object. <i>Compro il pane <u>per mia madre</u>.</i></li>
          </ul>
          <p class="text-sage-700 text-sm">A sentence can have both: <i>Le compro il pane</i> — <i>il pane</i> (direct) and <i>le</i> = to her (indirect).</p>
        `,
      },
      {
        title: "2. Replacing the noun with a pronoun",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>Direct:</b> <i>Vedo <u>il film</u> → <b>Lo</b> vedo.</i></li>
            <li><b>Indirect:</b> <i>Parlo <u>a Giovanni</u> → <b>Gli</b> parlo.</i></li>
            <li>Placement is the same as other object pronouns: before a conjugated verb (<i>Lo vedo</i>), or attached to an infinitive/gerund/informal command (<i>vederlo, vedendolo, dimmelo!</i>).</li>
          </ul>
        `,
      },
      {
        title: "3. Combining both — gli/le + lo → glielo",
        body: `
          <p class="text-sage-700 mb-2">When you use both pronouns, the order is <b>indirect before direct</b>. The <i>mi/ti/ci/vi</i> forms shift to <b>me/te/ce/ve</b>, and the third-person <b>gli/le</b> both fuse into one word <b>glie-</b>:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>Do il libro a Maria → Le do il libro → <b>Glielo</b> do.</i></li>
            <li><i>Ho mandato le foto a loro → <b>Gliele</b> ho mandate.</i></li>
            <li>With <i>mi/ti…</i>: <i>Me lo dai? · Te li mando.</i></li>
          </ul>
        `,
      },
      {
        title: "4. Dislocation — repeating for emphasis (it's normal!)",
        body: `
          <p class="text-sage-700 mb-2">Italian often <b>names the object and echoes it with a pronoun</b> — it's not a mistake, it's everyday spoken style (dislocazione):</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i><b>Il pane</b>, <b>l'</b>ho già comprato.</i> (Left dislocation — the direct object echoed by <i>lo</i>.)</li>
            <li><i><b>A Maria</b> <b>le</b> ho dato il regalo.</i> (The indirect object echoed by <i>le</i>.)</li>
          </ul>
          <p class="text-sage-700 text-sm mt-3"><b>Heads-up — agreement:</b> in the passato prossimo with <i>avere</i>, the participle agrees with a preceding <i>lo/la/li/le</i>: <i>Le foto? <b>Le</b> ho vist<b>e</b>.</i> (not <i>visto</i>).</p>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
